import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  NgZone,
  OnDestroy,
  OnInit,
  QueryList,
  resource,
  ViewChildren } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import {
  BackNavigable,
  BackNavigationService,
  queryParam,
  SOFTLINE_FEATURE_TITLE,
  TitleStore } from '@softline/application';
import { DateService, isIsoDate, IsoDate, Store } from '@softline/core';
import { SOFTLINE_FEATURE_WWS_BASISARTIKELSTAND } from '../../basisartikelstand.shared';
import { WwsBasisartikelstandStore } from '../../store/basisartikelstand.store';
import {
  ModalStore,
  NumberInputComponent,
  SOFTLINE_FEATURE_MODAL } from '@softline/ui-core';
import { Basisartikelstand, Untergruppe } from '../../types/basisartikelstand';
import { Router } from '@angular/router';
import { skip, Subscription } from 'rxjs';
import { ArtikelInformationen } from '../../types/artikel';
import { FormArray, FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { KommentarDialogComponent } from '../../dialogs/kommentar-dialog/kommentar-dialog.component';
import { ArtikelHinzufuegenDialogComponent } from '../../dialogs/artikel-hinzufuegen-dialog/artikel-hinzufuegen-dialog.component';
import { A11yModule, FocusOrigin } from '@angular/cdk/a11y';

import { RepeatDirective } from '@softline/ui-core';
import { TranslatePipe } from '@softline/ui-core';
@Component({
  selector: 'soft-uebersicht',
  imports: [CommonModule, ReactiveFormsModule, A11yModule,
    NumberInputComponent,
    RepeatDirective,
    TranslatePipe],
  providers: [DatePipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './uebersicht.component.html',
  styleUrls: ['./uebersicht.component.css'] })
export class UebersichtComponent implements OnInit, OnDestroy, BackNavigable {
  private readonly dateService = inject(DateService);

  private registerDateChange?: () => void;

  @ViewChildren(NumberInputComponent) items?: QueryList<NumberInputComponent>;

  private unregisterExtraArtikelControls: (() => void)[] = [];

  form: FormGroup = new FormGroup({
    datum: new FormControl(),
    hauptgruppen: new FormArray([]),
    id: new FormControl() });

  formSubcription?: Subscription;
  basisArtikelstandSubscription?: Subscription;

  readonly basisartikelstand$ = this.store.observe(
    SOFTLINE_FEATURE_WWS_BASISARTIKELSTAND,
    WwsBasisartikelstandStore.getters.data
  );

  @ViewChildren(NumberInputComponent) numberInputs?: QueryList<NumberInputComponent>;

  readonly trackByIndex = <T>(index: number, _: T) => index;

  private readonly queryDate = queryParam('date');

  private readonly date = computed(() => {
    const date = this.queryDate();
    if (!date || !isIsoDate(date)) return this.dateService.now();

    return date;
  });

  constructor(
    private store: Store,
    private zone: NgZone,
    private backNavigationService: BackNavigationService,
    private router: Router
  ) {}

  async ngOnInit(): Promise<void> {
    this.store.commit(
      SOFTLINE_FEATURE_TITLE,
      TitleStore.mutations.setSubTitle,
      'Tabellen-Übersicht'
    );

    this.backNavigationService.set(this);

    this.basisArtikelstandSubscription = this.basisartikelstand$
      .pipe(skip(1))
      .subscribe((artikelstand) => {
        this.form.patchValue(artikelstand, { onlySelf: true, emitEvent: false });
      });
  }

  readonly basisartikelstammRes = resource({
    params: () => this.date(),
    loader: async ({ params }) => {
      await this.onNavigatorChange(params);
    } });

  private async onFormChange(value: any): Promise<void> {
    console.log('onFormChange', value);
    await this.store.dispatch(
      SOFTLINE_FEATURE_WWS_BASISARTIKELSTAND,
      WwsBasisartikelstandStore.actions.update,
      {
        data: value,
        pathParams: {
          datum: this.date(), // format: yyyy-MM-dd
        } }
    );
  }

  ngOnDestroy(): void {
    this.registerDateChange?.();

    this.store.commit(SOFTLINE_FEATURE_TITLE, TitleStore.mutations.setSubTitle, '');

    if (this.formSubcription) this.formSubcription?.unsubscribe();

    if (this.basisArtikelstandSubscription)
      this.basisArtikelstandSubscription?.unsubscribe();

    this.backNavigationService.set(undefined);
  }

  async onNavigatorChange(date: string | IsoDate) {
    this.unregisterExtraArtikelControls.forEach((fn) => fn());
    this.unregisterExtraArtikelControls = [];

    const artikelstand = await this.store.dispatch(
      SOFTLINE_FEATURE_WWS_BASISARTIKELSTAND,
      WwsBasisartikelstandStore.actions.load,
      {
        pathParams: {
          datum: date, // format: yyyy-MM-dd
        } }
    );

    this.form = new FormGroup({
      datum: new FormControl(),
      hauptgruppen: new FormArray([]),
      id: new FormControl() });

    this.createHauptgruppenForm(artikelstand);
    this.form.patchValue(artikelstand, { onlySelf: true, emitEvent: false });

    if (this.formSubcription && !this.formSubcription.closed)
      this.formSubcription.unsubscribe();

    this.formSubcription = this.form.valueChanges.subscribe(async (value) =>
      this.onFormChange(value)
    );
    // this.focusFirstInput();
  }

  async navigateBack(): Promise<void> {
    await this.router.navigate(['/']);
  }

  private focusFirstInput(): void {
    this.zone.runOutsideAngular(() => {
      setTimeout(() => {
        const validInputs =
          this.items?.toArray().filter((o) => o.readonly() === false) ?? [];
        validInputs[0]?.inputElement?.focus();
      }, 10);
    });
  }

  selectTextOnFocus(origin: FocusOrigin, inputElement: HTMLInputElement | null): void {
    if (!origin || !inputElement) {
      inputElement?.setSelectionRange(0, 0);
      return;
    }

    // setTimeout is required in order to make the selection work correctly
    // without the setTimeout selection only works around 6 of 10 times
    this.zone.runOutsideAngular(() => {
      setTimeout(
        () => inputElement.setSelectionRange(0, inputElement?.value?.length ?? 0),
        1
      );
    });
  }

  createHauptgruppenForm(basisartikelstand: Basisartikelstand) {
    basisartikelstand.hauptgruppen.forEach((hauptgruppe) => {
      const group: FormGroup = new FormGroup({
        hauptgruppe: new FormControl(hauptgruppe.hauptgruppe),
        id: new FormControl(hauptgruppe.id),
        untergruppen: new FormArray([]),
        name: new FormControl(hauptgruppe.name) });

      (<FormArray>this.form.controls['hauptgruppen'])?.push(group, { emitEvent: false });

      this.createUntergruppenForm(
        hauptgruppe.untergruppen,
        <FormArray>group.controls['untergruppen']
      );
    });
  }

  createUntergruppenForm(untergruppen: Untergruppe[], formArray: FormArray) {
    untergruppen.forEach((untergruppe) => {
      const group: FormGroup = new FormGroup({
        id: new FormControl(untergruppe.id),
        artikelsumme: new FormArray([]),
        artikeluntergruppe: new FormControl(untergruppe.artikeluntergruppe),
        kzartikelgruppe: new FormControl(untergruppe.kzartikelgruppe),
        name: new FormControl(untergruppe.name),
        basisartikel: new FormArray([]),
        weitereArtikel: new FormArray([]) });

      formArray.push(group, { emitEvent: false });

      this.createArtikelForm(
        untergruppe,
        <FormArray>group.controls['basisartikel'],
        <FormArray>group.controls['artikelsumme'],
        <FormArray>group.controls['weitereArtikel']
      );
    });
  }

  createArtikelForm(
    untergruppe: Untergruppe,
    formArtikelArray: FormArray,
    formArtikelSummeArray: FormArray,
    formWeitereArtikelArray: FormArray
  ) {
    untergruppe.basisartikel.forEach((artikelInfo, index) => {
      this.createBasisArtikelForm(artikelInfo, index, formArtikelArray);
    });

    untergruppe.artikelsumme.forEach((summe) => {
      const group = new FormGroup({
        summe: new FormControl(summe.summe),
        summe2: new FormControl(summe.summe2),
        name: new FormControl(summe.name),
        key: new FormControl(summe.key) });

      formArtikelSummeArray?.push(group, { emitEvent: false });
    });

    untergruppe.weitereArtikel.forEach((artikelInfo, index) => {
      this.createBasisArtikelForm(artikelInfo, index, formWeitereArtikelArray);
    });
  }

  createBasisArtikelForm(
    artikelInfo: ArtikelInformationen,
    index: number,
    formArtikelArray: FormArray,
    remove: boolean = true
  ) {
    const group: FormGroup = new FormGroup({
      artikel: new FormGroup({
        id: new FormControl(artikelInfo.artikel.id),
        nummer: new FormControl(artikelInfo.artikel.nummer),
        artkurzbez: new FormControl(artikelInfo.artikel?.artkurzbez),
        arteh_erf: new FormGroup({
          arteh: new FormControl(artikelInfo.artikel.arteh_erf?.arteh),
          bezeichnung: new FormControl(artikelInfo.artikel.arteh_erf?.bezeichnung),
          id: new FormControl(artikelInfo.artikel.arteh_erf?.id) }),
        arteh_lag: new FormGroup({
          arteh: new FormControl(artikelInfo.artikel.arteh_lag?.arteh || ''),
          bezeichnung: new FormControl(artikelInfo.artikel.arteh_lag?.bezeichnung),
          id: new FormControl(artikelInfo.artikel.arteh_lag?.id) }) }),
      beweinv: new FormGroup({
        id: new FormControl(artikelInfo.beweinv?.id),
        menge: new FormControl(artikelInfo.beweinv?.menge),
        lagmenge: new FormControl(artikelInfo.beweinv?.lagmenge),
        kommentar: new FormControl(artikelInfo.beweinv?.kommentar || '') }),
      extra: new FormControl(artikelInfo.extra) });

    formArtikelArray?.push(group, { emitEvent: false });

    if (artikelInfo.extra && remove) {
      this.unregisterExtraArtikelControls.push(() =>
        formArtikelArray.removeAt(index, { emitEvent: false })
      );
    }
  }

  async openCommentDialog(kommentar: string, form: FormGroup<any>): Promise<void> {
    const result = await this.store.dispatch(
      SOFTLINE_FEATURE_MODAL,
      ModalStore.actions.open<string | null, object>(),
      {
        id: 'SOFTLINE_DIALOG_BASISARTIKELSTAND_KOMMENTAR',
        component: KommentarDialogComponent,
        data: {
          kommentar: kommentar },
        dismiss: true }
    );

    if (result === 'DISMISSED' || result === null) {
      return;
    }

    form.patchValue({
      beweinv: {
        kommentar: result ?? null } });
  }

  async openArtikelHinzufuegenDialog(
    form: FormGroup<{ weitereArtikel: FormArray; artikel: FormArray }>
  ): Promise<void> {
    const result = await this.store.dispatch(
      SOFTLINE_FEATURE_MODAL,
      ModalStore.actions.open<ArtikelInformationen[], object>(),
      {
        id: 'SOFTLINE_DIALOG_BASISARTIKELSTAND_ARTIKEL_HINZUFUEGEN',
        component: ArtikelHinzufuegenDialogComponent,
        data: {
          weitereArtikel: form.controls['weitereArtikel']?.value },
        dismiss: true }
    );

    if (result === 'DISMISSED' || result.length === 0) {
      return;
    }

    for (const basisartikel of result) {
      const group = new FormGroup({
        artikel: new FormGroup({
          id: new FormControl(basisartikel.artikel.id),
          nummer: new FormControl(basisartikel.artikel.nummer),
          artkurzbez: new FormControl(basisartikel.artikel.artkurzbez),
          arteh_erf: new FormGroup({
            arteh: new FormControl(basisartikel.artikel.arteh_erf?.arteh),
            bezeichnung: new FormControl(basisartikel.artikel.arteh_erf?.bezeichnung),
            id: new FormControl(basisartikel.artikel.arteh_erf?.id) }),
          arteh_lag: new FormGroup({
            arteh: new FormControl(basisartikel.artikel.arteh_lag?.arteh),
            bezeichnung: new FormControl(basisartikel.artikel.arteh_lag?.bezeichnung),
            id: new FormControl(basisartikel.artikel.arteh_lag?.id) }) }),
        beweinv: new FormGroup({
          id: new FormControl(basisartikel.beweinv.id),
          menge: new FormControl(basisartikel.beweinv.menge),
          lagmenge: new FormControl(basisartikel.beweinv.lagmenge),
          kommentar: new FormControl(basisartikel.beweinv.kommentar) }),
        extra: new FormControl(basisartikel.extra) });

      form.controls['basisartikel']?.push(group, { emitEvent: false });
      const index = form.controls['basisartikel']?.length - 1;

      this.unregisterExtraArtikelControls.push(() =>
        form.controls['basisartikel']?.removeAt(index, { emitEvent: false })
      );

      const weitereArtikelIndex = form.controls['weitereArtikel'].controls.findIndex(
        (o) => o.value.artikel.id === basisartikel.artikel.id
      );
      form.controls['weitereArtikel'].removeAt(weitereArtikelIndex);
    }

    const currentArtikel = form.controls['basisartikel'].value;
    form.patchValue({ artikel: [...currentArtikel, ...result] });
  }
}
